import { useMemo, useState } from 'react';
import { Button, Modal, Typography, message } from 'antd';
import { marked } from 'marked';
import './TermsOfUseModal.css';

const { Paragraph } = Typography;

marked.setOptions({
  breaks: true,
  gfm: true,
});

interface TermsOfUseModalProps {
  isOpen: boolean;
  markdown: string;
  isLoading: boolean;
  loadError: string | null;
  csrfToken?: string;
  onRetryLoad: () => void;
  onDeclined: () => void;
  onAccepted: (acceptedOn: string | null, version: string | null) => void;
}

function TermsOfUseModal({
  isOpen,
  markdown,
  isLoading,
  loadError,
  csrfToken,
  onRetryLoad,
  onDeclined,
  onAccepted,
}: TermsOfUseModalProps) {
  const [isAccepting, setIsAccepting] = useState(false);

  const termsHtml = useMemo(() => {
    if (!markdown) {
      return '';
    }
    const parsed = marked.parse(markdown) as string;
    return parsed.replace(/<a href/g, '<a target="_blank" rel="noopener noreferrer" href');
  }, [markdown]);

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      const response = await fetch('/auth/api/accept-terms-of-use', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Unable to accept terms of use');
      }

      const data = await response.json();
      onAccepted(data.accepted_on || null, data.version || null);
    } catch (error) {
      console.error('Terms of use acceptance failed:', error);
      message.error('Unable to accept the Terms of Use right now.');
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      title="Terms of Use Agreement"
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={760}
      footer={[
        loadError ? (
          <Button key="retry" onClick={onRetryLoad}>
            Retry
          </Button>
        ) : null,
        <Button key="decline" onClick={onDeclined} disabled={isAccepting}>
          Decline
        </Button>,
        <Button
          key="accept"
          type="primary"
          danger
          onClick={handleAccept}
          loading={isAccepting}
          disabled={isLoading || !!loadError || !markdown}
        >
          Accept
        </Button>,
      ]}
    >
      <Paragraph>
        Please review and accept the Terms of Use to continue using the application.
      </Paragraph>
      {isLoading ? (
        <div className="terms-of-use-modal__status">Loading Terms of Use...</div>
      ) : loadError ? (
        <div className="terms-of-use-modal__status terms-of-use-modal__status--error">{loadError}</div>
      ) : (
        <div
          className="terms-of-use-modal__content markdown-content"
          dangerouslySetInnerHTML={{ __html: termsHtml }}
        />
      )}
    </Modal>
  );
}

export default TermsOfUseModal;
